/**
 * What a widget *does*, as against what it is.
 *
 * Dimension 5 of the specification. The anatomy says a select has a popup and the state contract
 * says it may be open; neither says that clicking the trigger opens it, that Escape closes it, or
 * that closing puts focus back where the user left it. Those are the parts of a widget a user
 * actually experiences, and they were expressed only as the behaviour of two shared functions.
 *
 * Declared independently of those functions on purpose. A table derived from the implementation
 * checks nothing; this states what the transitions are supposed to be, and
 * `transitions.spec.mjs` holds {@link overlayLifecycleTransition} — the one every renderer routes
 * through — and {@link widgetKeyIntent} to it.
 */
import { MDY_VALUE_CONTRACTS } from "@modyra/core";
import { MDY_POPUP_OPENERS, MDY_WIDGET_CONTRACTS } from "./catalog.js";
import { MDY_FIELD_CAPABILITIES } from "./structure.js";
/**
 * A disabled widget does not transition at all.
 *
 * Stated as data rather than left implicit: "nothing happens" is a behaviour a renderer has to
 * implement, and a disabled trigger that still opens its popup is a real defect that looks like no
 * transition being declared.
 */
export const MDY_DISABLED_BLOCKS_TRANSITIONS = true;
/**
 * Whether a widget is open, given that a disabled one makes no move its table declares.
 *
 * The constant above says what a disabled widget does not do; this is what that means for the one
 * state it can already be *in*. Holding `open` while out of play is the same violation seen from the
 * other side: the panel is not the result of a transition it is about to make, it is the residue of
 * one it made while it still could.
 *
 * What it costs to get wrong is not tidiness. A document rule takes a field out of play when another
 * field changes, and the panel that stays is over a control nobody can operate — reachable by the
 * keyboard, describing choices that lead nowhere.
 *
 * Read where the state is assembled rather than answered by each renderer: three renderers appeared
 * to honour this because their panel is drawn inside the field and vanishes with the field's own
 * treatment, which is a consequence of placement rather than a decision — the same accident that
 * ADR 0206 ends for a field leaving the document.
 */
export function staysOpen(open, disabled) {
    return open && !(MDY_DISABLED_BLOCKS_TRANSITIONS && disabled);
}
function transitionsFor(kind) {
    const definition = MDY_WIDGET_CONTRACTS[kind];
    const opener = MDY_POPUP_OPENERS[kind];
    if (!definition.capabilities.overlay || !opener)
        return Object.freeze([]);
    const transitions = [
        { from: "closed", trigger: { type: "pointer", part: opener.opener }, to: "open" },
        // The same press again closes it: an opener is a toggle, not a one-way switch.
        //
        // Except where the opener is the control the user types into. A press there is the user placing
        // the caret, and answering it by closing the calendar takes the field away at the moment they
        // reached for it. The rule was written for a button and applied to every opener alike, and one
        // renderer implemented it literally while another did not — so the same click did different
        // things depending on who drew the widget, with the contract endorsing the worse of the two.
        ...(opener.typeable
            ? []
            : [{ from: "open", trigger: { type: "pointer", part: opener.opener }, to: "closed" }]),
        { from: "open", trigger: { type: "key", key: "Escape" }, to: "closed", restoresFocus: true },
    ];
    if (definition.capabilities.dismissOnOutsidePointer) {
        transitions.push({ from: "open", trigger: { type: "outside" }, to: "closed" });
    }
    return Object.freeze(transitions);
}
/** Every transition each kind admits. A kind with no overlay declares none — yet. */
export const MDY_WIDGET_TRANSITIONS = Object.freeze(Object.fromEntries(Object.keys(MDY_WIDGET_CONTRACTS).map((kind) => [kind, Object.freeze(transitionsFor(kind).map((entry) => Object.freeze({
        ...entry,
        // One level deeper than the entry itself: a trigger and a list of parts are objects the
        // caller holds a reference to, and an entry frozen around live members is not frozen.
        ...Object.fromEntries(Object.entries(entry)
            .filter(([, member]) => member !== null && typeof member === "object")
            .map(([key, member]) => [key, Object.freeze(member)])),
    })))])));
/** The transitions a kind admits from a given phase. */
export function transitionsFrom(kind, phase) {
    return MDY_WIDGET_TRANSITIONS[kind].filter((transition) => transition.from === phase);
}
/**
 * What a key does, per kind.
 *
 * `widgetKeyIntent` answered this for years without asking which widget it was looking at: every
 * kind but `number` was told ArrowDown means "move to the next option", so a text field navigated a
 * list it does not have, a textarea was told Enter opens something, and a slider — whose arrows must
 * change its value — was given list navigation instead of a step.
 *
 * What a key may do follows from what the kind *is*: a widget with options navigates them, one with
 * a range steps it, one with two states toggles, one with an overlay opens and closes it. Declaring
 * that per kind is what stops the answer from being the same everywhere.
 */
/**
 * A binding that answers *any* printable character rather than one named key.
 *
 * Type-ahead is the gesture every list has and no single key names: typing `r` moves to the first
 * option beginning with r. Declared as a key it would have to pick one letter and mean all of them —
 * a table that says one thing and intends another, which a tool reads literally and a person reads
 * charitably. This is the key field admitting it has no key.
 */
export const MDY_ANY_PRINTABLE_KEY = "*printable*";
/** Kinds whose value is chosen from a list the keyboard walks. */
const NAVIGATES_OPTIONS = Object.freeze([
    "select", "multiselect", "radio", "segmented", "datepicker", "daterange", "timepicker", "colors",
]);
/** Kinds whose value is a point on a range the keyboard nudges. */
const STEPS_A_RANGE = Object.freeze(["number", "slider"]);
/** Kinds with exactly two states, which Space flips. */
const TOGGLES = Object.freeze(["checkbox", "toggle", "radio", "segmented"]);
function keyboardFor(kind) {
    const overlay = MDY_WIDGET_CONTRACTS[kind].capabilities.overlay;
    const bindings = [];
    let tabDecidedLast = false;
    if (overlay) {
        // Whatever is held with it. Refusing the way out because somebody's hand was on a modifier is a
        // keyboard trap, and one that survives testing because nobody presses Escape with Command down.
        bindings.push({ key: "Escape", when: "open", intent: "cancel", restoresFocus: true, modifier: "any" });
        // Tab dismisses without taking focus back. Leaving it undeclared did not make the list stay
        // open — it made the *table* disagree with the policy the renderers actually call, so a renderer
        // built from the declared bindings alone left a popup floating over a form the user had left.
        //
        // Unless the popup has controls of its own to move between. A kind that declares an `actions`
        // bar has a confirm button in its overlay, and a Tab that dismisses makes that button
        // unreachable from a keyboard — so the widget's only way to commit is a pointer, which is
        // WCAG 2.1.1 and not a preference. There the key moves within the popup and the popup keeps it.
        //
        // Asked of the catalogue rather than of a list beside it: a kind that grows an action bar grows
        // this with it, and one that loses it loses this. `Escape` still cancels, in both shapes, and is
        // what a keyboard user leaves with.
        // Read as the paragraph above says it, rather than as the one bar that first needed it.
        //
        // `"actions" in parts` was the timepicker's own anatomy standing in for the rule, and it was
        // narrower than the sentence beside it: the colours panel holds a button for entering a custom
        // tint, has no `actions` bar, and was therefore classed as a panel you only choose from — so Tab
        // closed it before that button, the arrows never leave the swatch grid, and the control was
        // reachable with a pointer and with nothing else. ADR 0198.
        //
        // Derived from what the catalogue already declares: a part inside the popup whose element is a
        // button, which is not one of the choices, and which is **not repeated**. The last clause is the
        // anatomy doing the deciding — one action per panel can be a tab stop because Tab knows where it
        // arrives; one action per *row* cannot, since a stop that named the row would be one stop per row
        // and Tab would be a scroll. Those are reached by a key on the active row instead, which is a
        // different repair for a different shape.
        // Decided at the end rather than here, because the answer depends on every binding this kind
        // declares and only some of them exist yet: asked now, a panel whose header keys are pushed
        // further down reads as having no key at all, and Tab starts being held by a panel that should
        // let it go. The placeholder keeps this list's order; the answer replaces it once complete.
        tabDecidedLast = true;
        // Which part a key opens from, named rather than left to mean "anywhere on this widget". A
        // closed control used to be one thing to press; a multiselect's closed control now holds a strip
        // of chips with keys of their own, and a binding with no part claimed those too — so `Enter`
        // meant both "open the list" and "pick up this chip", decided by whichever handler ran first.
        // `MDY_POPUP_OPENERS` already says which part a person opens this kind with.
        const opensFrom = MDY_POPUP_OPENERS[kind]?.opener;
        const from = opensFrom === undefined ? {} : { on: opensFrom };
        bindings.push({ key: "Enter", when: "closed", intent: "open", ...from });
        bindings.push({ key: "Enter", when: "open", intent: "commit" });
        // Space, whose meaning is a function of the part it lands on rather than of the kind.
        //
        // In a text box it is a character — a date being typed, a filter being written — so no binding
        // claims it there, and the platform keeps it. On a thing with a highlight it is *this one*: the
        // day the reading position is on, the option under it. That is the distinction Enter does not
        // carry: **Enter means done, Space means this one**, and they collapse only where choosing is
        // being done. A range picker is where they come apart, which is why they are two bindings even
        // on the kinds where they do the same thing today.
        //
        // Declared on the part, so a kind whose panel holds a filter box answers Space with a space
        // while the same key over its options answers with a choice. ADR 0174.
        // `toggle` where the field can hold more than one — pick, move, pick, and the list stays open —
        // and `commit` where it holds one, because there choosing *is* being done. Asked of the value
        // contract's own shape rather than of a list of kinds.
        const holdsSeveral = (MDY_VALUE_CONTRACTS[kind]?.shape ?? "").endsWith("[]");
        for (const part of ["gridcell", "option"]) {
            if (!MDY_WIDGET_CONTRACTS[kind].structure.nodes.some((node) => node.part === part))
                continue;
            bindings.push({
                key: " ",
                when: "open",
                intent: part === "option" && holdsSeveral ? "toggle" : "commit",
                on: part,
            });
        }
        // The combobox pattern, and only for a kind that is one: pressing an arrow on a closed control
        // opens it rather than doing nothing, which is how a keyboard user reaches the list at all.
        //
        // Both directions, and neither carries a move. APG has the up arrow open *onto the last option*
        // and the down arrow onto the first — which is what happens here anyway, one layer down:
        // `listboxNavigationIndex` answers `ArrowUp` from nothing-active with the last option and
        // `ArrowDown` with the first. Declaring a second intent on the same key would restate that in a
        // place it can drift from.
        //
        // An overlay that holds no options is not a combobox and this does not apply to it. A calendar,
        // a clock face and a colour palette are dialogs a button opens: the reasoning above is about
        // reaching the first or last *option*, and there is no such list to arrive in. Declared for them,
        // it described a behaviour no renderer implemented and none intended to — three of them omitted
        // it independently, which is the evidence that read as three oversights and was one rule applied
        // where it does not belong.
        //
        // The test is whether the kind declares a `listbox` part, which is the catalogue already saying
        // which overlays hold a list — not a second list to keep in step with it. `NAVIGATES_OPTIONS` is
        // the wrong question here and reads like the right one: a calendar *is* walked with the arrow
        // keys, inside its grid, which is a different statement from the arrows reaching a list that is
        // not on screen yet.
        // The test is whether the popup holds *options* — the catalogue already saying which overlays
        // hold a list. It used to ask for a `listbox` part, and the multiselect lost its arrows the day
        // that part was retired: its popup still held the same options under a different part name, so
        // a person who learned `ArrowDown` on one combobox no longer had it on the other.
        //
        // A calendar, a clock face and a colour palette are dialogs a button opens: the reasoning is
        // about reaching the first or last *option*, and there is no such list to arrive in. None of
        // them declares one, so this asks the right question of them too.
        if ("option" in MDY_WIDGET_CONTRACTS[kind].parts) {
            bindings.push({ key: "ArrowDown", when: "closed", intent: "open", ...from });
            bindings.push({ key: "ArrowUp", when: "closed", intent: "open", ...from });
        }
        // Space opens too — but only where the opener is not a control the user types into. In a text
        // field the space bar is a space character, and a widget that opened its calendar instead would
        // be unable to accept "12 March". The keyboard policy has opened on Space for as long as it has
        // existed and this table claimed the key for nothing, so the two disagreed in the same way they
        // did over Tab; declaring it needed the opener to be able to say what it is.
        if (!MDY_POPUP_OPENERS[kind]?.typeable) {
            bindings.push({ key: " ", when: "closed", intent: "open", ...from });
        }
    }
    // Turning the page of a calendar: `PageDown` to the next month, `PageUp` to the one before.
    //
    // All three renderers do it and none was asked to. An adapter written from the contract alone
    // shipped a calendar that could not leave the month it opened on — the arrows walk within a month
    // and step across its edge one day at a time, which is a long way to reach next March.
    //
    // Keyed on the kind having a `grid`, which is what a month *is*: a calendar has one and a list does
    // not, so a page key means something here and nothing there.
    const turnsAPage = MDY_WIDGET_CONTRACTS[kind].structure.nodes.some((node) => node.part === "grid");
    if (turnsAPage) {
        bindings.push({ key: "PageDown", when: "open", intent: "move", by: 1, page: true, longStride: true });
        bindings.push({ key: "PageUp", when: "open", intent: "move", by: -1, page: true, longStride: true });
        // Sideways, which is half of walking a month. A week runs left to right and the table declared
        // only the vertical pair, so a person moving through a calendar with the keyboard could go up
        // and down a column and never along a row — and all three renderers answered the horizontal
        // arrows anyway, because a grid that cannot be walked sideways is not a grid.
        //
        // A day at a time, where the vertical pair moves a week: that is what the two axes of a month
        // are, and it is the same `by` the grid pattern gives every other two-dimensional widget.
        bindings.push({ key: "ArrowRight", when: "open", intent: "move", by: 1 });
        bindings.push({ key: "ArrowLeft", when: "open", intent: "move", by: -1 });
    }
    // Changing which view the calendar is showing: out to the months, out again to the years, and back
    // in the other direction.
    //
    // Keyed on the alternate views being declared, not on the grid: a kind may have a month to walk
    // without having anywhere else to go, and a key that changes to a view a kind does not draw is a
    // key that does nothing. The two views are named parts, so the anatomy decides this the way it
    // decides the page keys above.
    //
    // The vertical arrows, held with the platform's accelerator. Every bare arrow is spent on walking
    // the grid, and a bare binding refuses a press with that accelerator held, so the two cannot
    // collide: `matchesKeyGesture` answers the bare declaration only when nothing is held, and this
    // one only when something is. `primary` rather than a named `Ctrl` because the accelerator is the
    // platform's — the calendar this follows is the desktop one, and on a Mac that gesture is `Cmd`.
    //
    // `Shift` and `Alt` were the other candidates and both are unavailable for a reason worth stating
    // here rather than rediscovering: a bare binding's refusal counts only `Ctrl` and `Meta`, so a
    // binding declared with `Shift` or `Alt` on a key already declared bare would be shadowed by it and
    // do nothing at all.
    const zoomsAView = ["monthPicker", "yearPicker"].every((part) => part in MDY_WIDGET_CONTRACTS[kind].parts);
    if (zoomsAView) {
        bindings.push({ key: "ArrowUp", when: "open", intent: "view", by: 1, modifier: "primary" });
        bindings.push({ key: "ArrowDown", when: "open", intent: "view", by: -1, modifier: "primary" });
    }
    if (NAVIGATES_OPTIONS.includes(kind)) {
        for (const key of ["ArrowDown", "ArrowUp"]) {
            bindings.push({ key, ...(overlay ? { when: "open" } : {}), intent: "move" });
        }
        // Home and End jump to the first and last option, which is the listbox and grid patterns. A
        // radio group is neither: APG gives it Tab, Space and the four arrows, and the arrows both move
        // and select, so there is no separate reading position for a jump to land on.
        //
        // Declared for one anyway, it describes a behaviour no renderer implements. That every
        // independent implementation of the same declaration omits the same binding is evidence about
        // the declaration, not about the implementations: one rule applied where it does not belong,
        // which is the mistake `NAVIGATES_OPTIONS` invited once before and ADR 0021 records — a kind
        // that walks its options with the arrows is not thereby a kind with a list to jump through.
        //
        // Asked of the catalogue rather than of a second list, so a kind that stops being a radio group
        // moves this with it.
        const isRadioGroup = Object.values(MDY_WIDGET_CONTRACTS[kind].parts)
            .some((declared) => declared.role === "radiogroup");
        // All four arrows, on a radio group, whichever way it is laid out.
        //
        // **The layout is a visual choice, and somebody who cannot see it does not know it.** A screen
        // reader announces "group, 1 of 4" and says nothing about a row or a column, so a person presses
        // whichever arrow comes to hand and it has to work. A group answering only its own axis makes
        // them guess an axis that was never announced.
        //
        // The paragraph above already said APG gives a radio group *the four arrows*, and this table
        // declared two of them — so one renderer honoured all four and the other two did not, and the
        // gesture somebody learned in one was gone in another. The contract was short, not the renderer
        // long, which is the direction that decides who has to change.
        if (isRadioGroup) {
            for (const key of ["ArrowRight", "ArrowLeft"]) {
                bindings.push({ key, intent: "move" });
            }
        }
        if (!isRadioGroup) {
            for (const key of ["Home", "End"]) {
                bindings.push({ key, ...(overlay ? { when: "open" } : {}), intent: "move" });
            }
        }
    }
    if (STEPS_A_RANGE.includes(kind)) {
        // Only the arrows. Home and End on a range mean "go to the minimum" and "to the maximum", and
        // the intent vocabulary has no word for that — declaring them as `step` would say something
        // untrue rather than leave a gap on the record.
        bindings.push({ key: "ArrowUp", intent: "step" }, { key: "ArrowDown", intent: "step" });
    }
    // Moving a chosen value, on the chip that stands for it.
    //
    // A grab rather than a modifier. `Alt`+arrow is Back and Forward in every major browser on Windows
    // and Linux: it worked here only because `preventDefault` suppressed the platform's own gesture,
    // and it taught a keystroke that on any other focused element throws away the form being filled
    // in. `Enter` picks the chip up, the bare arrows carry it, `Enter` puts it down and `Escape` puts
    // it back — no modifier, so nothing to collide with on any platform.
    //
    // A grab is also a *state*, which is what the modifier could never be: it can be announced — "Roma
    // grabbed, 3 of 12" — and it can be cancelled. Both are things a person rearranging a list by
    // keyboard has no other way to get.
    //
    // Declared for a kind whose value is a list a person arranges, which the catalogue says by
    // holding a `chips` part: a set of filters has an order nobody chose and nothing to reorder.
    if ("chips" in MDY_WIDGET_CONTRACTS[kind].parts) {
        // Picking up and putting down are one key, because they are one state seen from its two ends.
        bindings.push({ key: "Enter", when: "closed", intent: "grab", on: "chip", requires: MDY_FIELD_CAPABILITIES.reorderable });
        // Putting it back. Only while something is held: the popup is closed by then — a grab cannot
        // begin while it is open — so this cannot be the same press that dismisses an overlay.
        bindings.push({ key: "Escape", when: "closed", intent: "cancel", on: "chip", requires: MDY_FIELD_CAPABILITIES.reorderable, modifier: "any" });
        // The arrows are declared once, below, as what moves the reading position. Held, they carry the
        // chip instead — the same movement with the grab's subject rather than the cursor's. Declaring
        // them twice and telling the two apart by the grab would put a state the table cannot see into
        // the table, and leave `keyBindingFor` answering whichever row was written first.
        // Moving *between* chips, and removing the one you are on. Declared `when: "closed"`, because
        // while the popup is showing the arrows belong to the list a person is choosing from — the same
        // key in two places is what the phase exists to separate.
        //
        // The strip is one tab stop with a roving index, not one stop per chip. Twelve chosen values
        // were twenty-six presses to get past the field, and the cost of leaving a control must not
        // grow with what it holds.
        bindings.push({ key: "ArrowLeft", when: "closed", intent: "move", by: -1, on: "chip" });
        bindings.push({ key: "ArrowRight", when: "closed", intent: "move", by: 1, on: "chip" });
        bindings.push({ key: "Home", when: "closed", intent: "move", by: -1, toEnd: true, on: "chip" });
        bindings.push({ key: "End", when: "closed", intent: "move", by: 1, toEnd: true, on: "chip" });
        // Taking one off from the keyboard, both spellings: `Backspace` is what a person reaches for
        // The quantity, on a chip that has one. ADR 0138 took the `spinbutton` role off the chip because
        // a control cannot be both the item at position 3 of 12 and the number 3 of a range — it did not
        // take the quantity away, and the record says so. The ± controls are `tabindex="-1"` pointer
        // affordances, so without these two keys a quantity is reachable by pointer and by nothing else:
        // WCAG 2.1.1, which is not a cost any record traded for a role.
        //
        // They collide with nothing. The strip's own arrows are left and right; the `open` bindings that
        // once claimed every part now name the control they open from.
        // Gated on the field counting: with a value held once and no quantity, these keys mean nothing,
        // and a gesture offered where there is nothing to do is a promise the widget cannot keep. The
        // steppers themselves are gated on the same word, so the key and the button appear together.
        bindings.push({ key: "ArrowUp", when: "closed", intent: "step", on: "chip", requires: MDY_FIELD_CAPABILITIES.countable });
        bindings.push({ key: "ArrowDown", when: "closed", intent: "step", on: "chip", requires: MDY_FIELD_CAPABILITIES.countable });
        // and `Delete` is what the platform's own lists answer to.
        // The same quantity, on the option in the open list, which had no key at all.
        //
        // `optionStep` is drawn inside each option while the panel is open and is a `tabindex="-1"`
        // pointer affordance like the chip's — so it was operable with a pointer and with nothing else,
        // for the same reason and in the same words as the chip's was before these two keys existed.
        //
        // A key rather than a tab stop, because this is one action **per row**: a stop that named the row
        // would be one stop per row and Tab would become a scroll. That is the criterion ADR 0198 draws,
        // and it is why this kind stays in the family whose Tab dismisses rather than joining the one
        // that keeps it.
        //
        // Left and right, measured against what this kind already declares while open — `Escape`, `Tab`,
        // `Enter`, `Space` on an option, `ArrowUp`/`ArrowDown`/`Home`/`End` for moving, and any printable
        // character for type-ahead. The vertical axis walks the list, so the horizontal one is free for
        // the control on the row the walk is standing on. `+` and `−` were the alternative and are not
        // available: they are printable, and the type-ahead binding above would eat them before this one
        // was asked.
        bindings.push({ key: "ArrowRight", when: "open", intent: "step", on: "option", by: 1, requires: MDY_FIELD_CAPABILITIES.countable });
        bindings.push({ key: "ArrowLeft", when: "open", intent: "step", on: "option", by: -1, requires: MDY_FIELD_CAPABILITIES.countable });
        bindings.push({ key: "Backspace", when: "closed", intent: "remove", on: "chip" });
        bindings.push({ key: "Delete", when: "closed", intent: "remove", on: "chip" });
        // The way back, from the keyboard, using the gesture every application on the platform already
        // means by it. Declared for the control rather than for the button that offers it: the offer
        // appears at the field's trailing edge after a value goes, and the person who wants it back is
        // standing wherever the removal left them — which is not there. A shortcut reachable only from
        // the control it undoes the need for is a shortcut for nobody.
        bindings.push({ key: "z", modifier: "primary", intent: "undo", awaits: "wayBack" });
    }
    if (TOGGLES.includes(kind))
        bindings.push({ key: " ", intent: "toggle" });
    // Type-ahead, where the kind holds a list of options to jump within.
    //
    // All three renderers implement it and none declared it, so a check counting what a renderer
    // claims against what the contract says reported it as a renderer doing more than it was asked.
    // It was the contract doing less: the gesture is what every list on every platform answers to, and
    // a person who types `r` expects to land on an option beginning with r.
    //
    // Declared with the key that says it has no key, rather than picking a letter to stand for the
    // alphabet.
    //
    // **Both phases where one choice is held, open only where several are.** A closed single-choice
    // control answers a letter by moving to that option — every native `<select>` does it, and so does
    // the framework-free renderer — so restricting the binding to the open phase said the gesture was
    // not owed at a control that has always offered it. Where several choices are held there is no
    // "the" choice for a letter to move, and the strip has its own use for keys, so it stays open-only
    // until something measures otherwise.
    //
    // Only where the kind holds a *list of named choices*, which is narrower than "navigates options":
    // a calendar walks its cells with the arrows and has nothing to type at — a date is not a word, and
    // typing `a` at a grid of numbers should reach the platform rather than be swallowed.
    //
    // Keyed on the part that *is* the list rather than on a role: the two kinds that have one give it
    // different roles — a listbox where choices are exclusive, a group where they are not — so a role
    // test covers one and misses the other while looking like it covers both.
    const hasLabelledOptions = MDY_WIDGET_CONTRACTS[kind].structure.nodes
        .some((node) => node.part === "options");
    if (hasLabelledOptions) {
        const holdsOneChoice = !MDY_WIDGET_CONTRACTS[kind].structure.nodes.some((node) => node.part === "chips");
        bindings.push({
            key: MDY_ANY_PRINTABLE_KEY,
            ...(overlay && !holdsOneChoice ? { when: "open" } : {}),
            intent: "typeahead",
        });
    }
    // Now that every binding exists, the panel can be asked whether it holds an act with no key —
    // which is what decides whether Tab is a way out or a way around.
    if (tabDecidedLast) {
        const keepsFocus = popupHoldsAnAction(kind, bindings);
        bindings.push(keepsFocus
            ? { key: "Tab", when: "open", intent: "move" }
            : { key: "Tab", when: "open", intent: "cancel", restoresFocus: false });
    }
    return Object.freeze(bindings);
}
/** Every key each kind answers to. A kind that answers to none declares none. */
/** The elements a part is drawn as when it is something a person operates. */
const OPERABLE_ELEMENTS = new Set(["button"]);
/** The elements a part is drawn as when it is one of the things being chosen. */
const CHOICE_ELEMENTS = new Set(["option", "gridcell", "radio"]);
/**
 * Whether a kind's popup holds an action of its own — something to operate that is not a choice.
 *
 * Walks the parents the structure declares rather than matching a name: `popup` and `dialog` are the
 * containers a panel is, and `content` is deliberately not among them — two kinds use that name for
 * different things, the timepicker's inner box and the file field's list of chosen files, and
 * admitting it put a field with no panel at all into the answer.
 */
export function popupHoldsAnAction(kind, 
/**
 * The bindings to judge the exemption against.
 *
 * Passed in rather than read from the published table because this is asked *while* that table is
 * built: reading it here is a module consulting its own half-built self, which fails at load.
 */
bindings = MDY_WIDGET_KEYBOARD[kind] ?? []) {
    const nodes = MDY_WIDGET_CONTRACTS[kind].structure.nodes;
    const byPart = new Map(nodes.map((node) => [String(node.part), node]));
    const insidePopup = (part) => {
        let at = byPart.get(part);
        const seen = new Set();
        while (at?.parent !== undefined && !seen.has(at.part)) {
            seen.add(at.part);
            if (at.parent === "popup" || at.parent === "dialog")
                return true;
            at = byPart.get(String(at.parent));
        }
        return false;
    };
    return nodes.some((node) => OPERABLE_ELEMENTS.has(String(node.element))
        && !CHOICE_ELEMENTS.has(String(node.element))
        && node.repeated !== true
        && !partIsReachedByAKey(node, bindings)
        && insidePopup(String(node.part)));
}
/**
 * Whether the act a part performs is one a declared key already performs while the panel is open.
 *
 * WCAG asks that an act be reachable from a keyboard, not that it be a Tab stop. A calendar's header
 * pages the month and switches the view, and the kind declares keys that do both while the panel is
 * open — so those acts are reachable without the panel swallowing Tab, and Tab keeps meaning
 * "leave", which is the one thing it means everywhere. A part that performs no act, or whose act no
 * key covers, answers `false`.
 *
 * Derived, not listed: it holds only while a binding covering the act exists, and of the same size —
 * a part that pages is covered only by a key that pages. Take the key away and the part is operable
 * with nothing reaching it, the panel holds Tab again, and the exemption dies with its own reason.
 *
 * One notion, asked here by the panel deciding Tab and by anything judging a single part: a second
 * copy of this rule is where the two would come to disagree.
 */
export function partIsReachedByAKey(node, bindings) {
    return node.performs !== undefined
        && bindings.some((binding) => binding.intent === node.performs
            && binding.when === "open"
            && (node.performsPage !== true || binding.page === true));
}
export const MDY_WIDGET_KEYBOARD = Object.freeze(Object.fromEntries(Object.keys(MDY_WIDGET_CONTRACTS).map((kind) => [kind, Object.freeze(keyboardFor(kind).map((entry) => Object.freeze({
        ...entry,
        // One level deeper than the entry itself: a trigger and a list of parts are objects the
        // caller holds a reference to, and an entry frozen around live members is not frozen.
        ...Object.fromEntries(Object.entries(entry)
            .filter(([, member]) => member !== null && typeof member === "object")
            .map(([key, member]) => [key, Object.freeze(member)])),
    })))])));
export function keyBindingFor(kind, key, open, 
/**
 * Where the person is. Absent means the control, which is what answers a key by default.
 *
 * A binding declared `on` another part is invisible from the control and the only answer from
 * that part, so one key can mean two things without either declaration shadowing the other.
 */
on) {
    const phase = open ? "open" : "closed";
    // A press carries what was held with it; a literal carries nothing and is treated as the bare key
    // it names. `matchesKeyGesture` is what decides the modifier, and this is the road it was missing:
    // published as the answer to this question, it was reachable from no path that asked it, so a
    // binding declared bare answered `Cmd+Space` and `Cmd+ArrowDown` as though they were plain.
    const press = typeof key === "string" ? null : key;
    const name = typeof key === "string" ? key : key.key;
    // Asked without a part, the question is "what does this key do at the control" — and the part a
    // person opens this kind with *is* the control for that purpose. So a binding declared on the
    // opener answers a control-level question, and one declared on any other part does not: a chip's
    // keys stay the chip's, which is what keeps one key from meaning two things in one place.
    const opener = on === undefined ? MDY_POPUP_OPENERS[kind]?.opener : undefined;
    /**
     * Whether this binding answers this key.
     *
     * A named key matches itself. {@link MDY_ANY_PRINTABLE_KEY} matches any single character a person
     * can type — which is what the type-ahead gesture is, and what no named key could stand for.
     *
     * A named key wins where both could answer, because `find` returns the first and the printable
     * binding is pushed last: a kind that one day declares a letter for something of its own keeps it,
     * and type-ahead takes the rest.
     */
    const answers = (binding) => {
        const named = binding.key === name
            || (binding.key === MDY_ANY_PRINTABLE_KEY && [...name].length === 1 && name !== " ");
        if (!named)
            return false;
        // The modifier is read against the character pressed: the printable binding's own key is a
        // stand-in that no press ever equals, so compared as written it refused every real keystroke.
        return press === null || matchesKeyGesture({ ...binding, key: name }, press);
    };
    return MDY_WIDGET_KEYBOARD[kind].find((binding) => answers(binding)
        && (binding.when === undefined || binding.when === phase)
        && ((binding.on ?? undefined) === on || (opener !== undefined && binding.on === opener))) ?? null;
}
/**
 * Whether a keyboard event is the gesture a binding declares.
 *
 * The platform test lives here and not in a renderer, because "the primary modifier" is one fact
 * about the machine and three copies of it drift the moment one is written from memory. It is read
 * from the event rather than from a user-agent string: `metaKey` is the accelerator where the
 * platform uses it and `ctrlKey` where it does not, and an event carries both — so a gesture is
 * recognised by what was actually held rather than by what the platform was guessed to be.
 *
 * Both are accepted, and that is deliberate rather than lax. A person on a Mac with a keyboard
 * mapped for another platform, or a remote session, holds the one their muscle memory knows; the
 * cost of accepting the other is a shortcut firing on a combination nothing else on the page claims.
 *
 * A binding with no modifier is a bare press, and a bare press is not this gesture: `z` alone must
 * not undo, or typing into a field beside the control would.
 */
export function matchesKeyGesture(binding, event) {
    if (binding.key.toLowerCase() !== event.key.toLowerCase())
        return false;
    const held = event.ctrlKey === true || event.metaKey === true;
    if (binding.modifier === "any")
        return true;
    if (binding.modifier === "primary")
        return held && event.altKey !== true && event.shiftKey !== true;
    return !held;
}
/**
 * How a control is operated, in one sentence, derived from the table above.
 *
 * Nothing else on a page says the key map exists. It is discoverable by guessing — the outside view's
 * largest finding — and a person who does not guess has a control they can see and cannot operate.
 *
 * Derived rather than written beside the table, because a sentence naming keys *is* a copy of the key
 * map: it goes stale the moment a binding moves, and this project has now found that shape five times
 * in five places. Derived, "it holds for every consumer" is true by construction rather than by
 * discipline.
 *
 * What it leaves out is as deliberate as what it says:
 *
 * - **Keys that need a capability the field did not ask for.** `reorderable` is opt-in, and a legend
 *   listing a key that does nothing on this control is worse than no legend — it was the likeliest
 *   reading of "reordering does not work".
 * - **Keys answered on a part that is not there.** A chip's keys mean nothing to a field holding no
 *   chips.
 * - **The keys every control shares.** Tab moves between fields everywhere; a sentence that says so
 *   on each of them is noise a reader learns to skip, and the ones that matter are inside it.
 */
export function widgetKeyGuide(kind, messages, options = {}) {
    const frames = {
        open: messages.keyGuideOpen,
        move: messages.keyGuideMove,
        step: messages.keyGuideStep,
        toggle: messages.keyGuideToggle,
        commit: messages.keyGuideCommit,
        cancel: messages.keyGuideCancel,
        remove: messages.keyGuideRemove,
        grab: messages.keyGuideGrab,
    };
    const named = (key) => (key === " " ? messages.keyGuideSpace : key);
    // One clause per intent, in the order the frames declare them, so two kinds that answer the same
    // keys read the same way round.
    const phase = options.open === true ? "open" : "closed";
    const opener = MDY_POPUP_OPENERS[kind]?.opener;
    const keysByIntent = new Map();
    for (const binding of MDY_WIDGET_KEYBOARD[kind]) {
        if (binding.when !== undefined && binding.when !== phase)
            continue;
        if (binding.requires !== undefined && !(options.capabilities ?? []).includes(binding.requires))
            continue;
        if (binding.on !== undefined && binding.on !== opener
            && options.parts !== undefined && !options.parts.includes(binding.on))
            continue;
        if (frames[binding.intent] === undefined)
            continue;
        const already = keysByIntent.get(binding.intent) ?? [];
        if (!already.includes(binding.key))
            already.push(binding.key);
        keysByIntent.set(binding.intent, already);
    }
    const clauses = [];
    for (const intent of Object.keys(frames)) {
        const keys = keysByIntent.get(intent);
        if (keys === undefined || keys.length === 0)
            continue;
        clauses.push(frames[intent].replace("{keys}", keys.map(named).join(messages.keyGuideOr)));
    }
    return clauses.join(messages.keyGuideJoin);
}
